import React, { useState } from 'react';
import { Check, CreditCard, Shield } from 'lucide-react';
import PaymentModal from './PaymentModal';
import { USER_LIMITS } from '../lib/types';

interface PricingToggleProps {
  isAnnual: boolean;
  onChange: (isAnnual: boolean) => void;
}

const PricingToggle: React.FC<PricingToggleProps> = ({ isAnnual, onChange }) => {
  return (
    <div className="flex items-center justify-center space-x-4 mb-12">
      <span className={`text-sm ${isAnnual ? 'text-[#c41d71] font-semibold' : 'text-gray-500'}`}>
        Facturation annuelle
        {isAnnual && <span className="ml-2 text-green-600">(économisez jusqu'à 25%)</span>}
      </span>
      <button
        onClick={() => onChange(!isAnnual)}
        className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#c41d71] focus:ring-offset-2"
        style={{ 
          background: isAnnual 
            ? 'linear-gradient(to right, #c41d71, #c85c44)' 
            : '#94A3B8' 
        }}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
            isAnnual ? 'translate-x-6' : 'translate-x-1'
          }`}
        />
      </button>
      <span className={`text-sm ${!isAnnual ? 'text-[#c41d71] font-semibold' : 'text-gray-500'}`}>
        Facturation mensuelle
      </span>
    </div>
  );
};

const PricingSection: React.FC = () => {
  const [isAnnual, setIsAnnual] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [selectedPlanPrice, setSelectedPlanPrice] = useState<string>("");

  const plans = [
    {
      id: 'trial',
      name: 'Essai',
      monthlyPrice: 0,
      yearlyPrice: 0,
      description: 'Découvrez notre outil gratuitement',
      features: [
        'Génération IA',
        'Analyse PDF',
        'Export/partage',
        'Connexion LinkedIn',
        'Sauvegarde',
      ],
      users: 1,
      cta: '🎁 Découvrir l\'outil',
      badge: 'Gratuit'
    },
    {
      id: 'starter',
      name: 'Starter',
      monthlyPrice: 9.90,
      yearlyPrice: 80,
      description: 'Pour les recruteurs occasionnels',
      features: [
        'Tout de l\'essai +',
        'Statistiques avancées',
        '10 offres par mois',
        'Support prioritaire',
        'Personnalisation avancée'
      ],
      users: 1,
      cta: '💡 Mode Starter',
      savings: '20€'
    },
    {
      id: 'pro',
      name: 'Pro',
      monthlyPrice: 19.90,
      yearlyPrice: 150,
      description: 'Pour les recruteurs réguliers',
      features: [
        'Tout du Starter +',
        'Offres illimitées',
        'API LinkedIn',
        'Statistiques avancées',
        'Support dédié'
      ],
      users: 1,
      cta: '🔥 Accélérer mes recrutements',
      isPopular: true,
      savings: '89€'
    },
    {
      id: 'cabinet',
      name: 'Cabinet',
      monthlyPrice: 15,
      yearlyPrice: 90,
      description: 'Pour les cabinets de recrutement',
      features: [
        'Tout du Pro +',
        'Multi-utilisateurs (3)',
        'Anonymisation des offres',
        'Statistiques par consultant',
        'Formation personnalisée'
      ],
      users: 3,
      cta: '🎯 Pour les équipes',
      savings: '90€'
    }
  ];

  const handleSelectPlan = (planId: string, price: string) => {
    setSelectedPlan(planId);
    setSelectedPlanPrice(price);
    setIsPaymentModalOpen(true);
  };

  return (
    <section id="pricing-section" className="py-20 bg-gray-50 scroll-mt-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Choisissez votre plan et passez à la vitesse supérieure
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Tous nos plans incluent la génération IA, l'export PDF et la connexion LinkedIn.
          </p>
        </div>

        <PricingToggle isAnnual={isAnnual} onChange={setIsAnnual} />

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {plans.map((plan) => (
            <div
              key={plan.id}
              className={`relative bg-white rounded-2xl shadow-lg overflow-hidden border-2 transition-all duration-300 hover:scale-105 ${
                plan.isPopular ? 'border-[#c41d71]' : 'border-transparent'
              }`}
            >
              {plan.isPopular && (
                <div 
                  className="absolute top-0 right-0 text-white px-4 py-1 rounded-bl-lg text-sm font-medium"
                  style={{ background: 'linear-gradient(to right, #c41d71, #c85c44)' }}
                >
                  Le plus populaire
                </div>
              )}
              {plan.badge && (
                <div className="absolute top-0 left-0 bg-green-500 text-white px-4 py-1 rounded-br-lg text-sm font-medium">
                  {plan.badge}
                </div>
              )}

              <div className="p-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                <p className="text-gray-600 mb-4">{plan.description}</p>
                
                <div className="mb-6">
                  <div className="flex items-end">
                    <span className="text-4xl font-bold text-[#c41d71]">
                      {isAnnual ? plan.yearlyPrice : plan.monthlyPrice}€
                    </span>
                    <span className="text-gray-500 ml-2">
                      {isAnnual ? '/an' : '/mois'}
                    </span>
                  </div>
                  {isAnnual && plan.savings && (
                    <p className="text-green-600 text-sm mt-2">
                      Économisez {plan.savings} par an
                    </p>
                  )}
                </div>

                <ul className="space-y-4 mb-8">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-center">
                      <Check className="w-5 h-5 text-[#c41d71] mr-3 flex-shrink-0" />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => handleSelectPlan(
                    plan.id, 
                    isAnnual ? plan.yearlyPrice.toString() : plan.monthlyPrice.toString()
                  )}
                  className="w-full text-white py-3 px-4 rounded-lg transition-all duration-300 flex items-center justify-center space-x-2 font-bold hover:opacity-90 transform hover:-translate-y-1"
                  style={{ background: 'linear-gradient(to right, #c41d71, #c85c44)' }}
                >
                  <CreditCard className="w-5 h-5" />
                  <span>{plan.cta}</span>
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-16 max-w-3xl mx-auto rounded-xl p-8" style={{ background: 'linear-gradient(to right, rgba(196, 29, 113, 0.1), rgba(200, 92, 68, 0.1))' }}>
          <div className="flex items-start">
            <Shield className="w-8 h-8 text-[#c41d71] mr-6 flex-shrink-0" />
            <div>
              <h3 className="text-xl font-semibold mb-2" style={{ color: '#c41d71' }}>
                Satisfaction garantie
              </h3>
              <p className="text-gray-700">
                Nous sommes convaincus que vous allez adorer notre service. Si vous n'êtes pas satisfait dans les 14 premiers jours, nous vous remboursons intégralement.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Payment Modal */}
      <PaymentModal 
        isOpen={isPaymentModalOpen}
        onClose={() => setIsPaymentModalOpen(false)}
        planId={selectedPlan || ""}
        planPrice={selectedPlanPrice}
        isAnnual={isAnnual}
      />
    </section>
  );
};

export default PricingSection;